import { normalizeTextToWords } from '../../picklist/utils';
import { parsePotentialPicklistCommand } from './parsePotentialPicklistCommand';
import { calculateFuzzyScore } from './picklistFuzzyScoring';

export type PicklistAdvisorCommandStableTextPrefixMatch = Readonly<{
  confidence: number;
  consumedPrefixText: string;
  postMatchText: string;
}>;

const MIN_ADVISOR_COMMAND_PREFIX_CONFIDENCE = 0.65;

const getWordSpans = (text: string): Array<{ word: string; end: number }> =>
  Array.from(text.matchAll(/\S+/g)).map((match) => ({
    word: match[0],
    end: (match.index ?? 0) + match[0].length,
  }));

const getAdvisorCommandScoringTargets = (advisorCommandText: string): string[] => {
  const advisorWords = advisorCommandText.trim().split(/\s+/).filter(Boolean);
  if (advisorWords.length === 0) return [];

  const scoringTargets = new Set([advisorWords.join(' ')]);
  const lastWord = advisorWords[advisorWords.length - 1];
  const lastNormalizedWord = normalizeTextToWords(lastWord ?? '')[0];

  // The existing fuzzy scorer normalizes "one" to "1" for phonetics, so include common
  // spoken-shape variants for command-prefix scoring without changing global number parsing.
  if (lastNormalizedWord === '1') {
    ['one', 'won', 'own'].forEach((variant) => {
      scoringTargets.add([...advisorWords.slice(0, -1), variant].join(' '));
    });
  }

  return Array.from(scoringTargets);
};

export const getAdvisorCommandStableTextPrefixMatch = ({
  stableText,
  advisorCommandText,
}: {
  stableText: string;
  advisorCommandText: string;
}): PicklistAdvisorCommandStableTextPrefixMatch | null => {
  const advisorWords = normalizeTextToWords(advisorCommandText);
  const advisorPicklistCommand = parsePotentialPicklistCommand(advisorWords);
  if (advisorPicklistCommand == null || advisorPicklistCommand.wordIndex !== 0) {
    return null;
  }
  if (advisorWords.length === 0) return null;

  const stableWordSpans = getWordSpans(stableText);
  if (stableWordSpans.length < advisorWords.length) {
    return null;
  }

  const stablePrefixText = stableWordSpans
    .slice(0, advisorWords.length)
    .map(({ word }) => word)
    .join(' ');
  const stablePrefixWords = normalizeTextToWords(stablePrefixText);
  const stablePicklistCommand = parsePotentialPicklistCommand(stablePrefixWords);
  if (stablePicklistCommand == null || stablePicklistCommand.wordIndex !== 0) {
    return null;
  }

  const confidence = Math.max(
    ...getAdvisorCommandScoringTargets(advisorCommandText).map((target) =>
      calculateFuzzyScore(stablePrefixText, target)
    )
  );
  if (confidence < MIN_ADVISOR_COMMAND_PREFIX_CONFIDENCE) {
    return null;
  }

  const consumedPrefixEnd = stableWordSpans[advisorWords.length - 1]?.end;
  if (consumedPrefixEnd == null) {
    return null;
  }

  return {
    confidence,
    consumedPrefixText: stableText.slice(0, consumedPrefixEnd).trim(),
    postMatchText: stableText.slice(consumedPrefixEnd),
  };
};
